import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  User, 
  FileText, 
  GraduationCap,
  LogOut,
  Menu,
  ChevronDown,
  ChevronRight,
  UserCircle,
  FileUp,
  Stamp,
  Calendar,
  Receipt,
  BookOpen,
  Upload
} from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

export default function AlumnoPortalLayout() {
  const { user, logout, isLoading } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [autoservicioOpen, setAutoservicioOpen] = useState(true);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4">
            <img src="/qcampusone-logo.png" alt="QCampusOne" className="w-12 h-12 object-contain" />
          </div>
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Get tenant from localStorage or redirect to tenant selector
    const currentTenant = localStorage.getItem('currentTenantSlug') || 'testcampus';
    return <Navigate to={`/${currentTenant}/login`} replace />;
  }

  const navItems = [
    { 
      type: 'link',
      path: '/alumno/dashboard', 
      label: 'Dashboard', 
      icon: LayoutDashboard 
    },
    { 
      type: 'link',
      path: '/alumno/kardex', 
      label: 'Kardex', 
      icon: GraduationCap 
    },
  ];

  const autoservicioItems = [
    { 
      path: '/alumno/perfil', 
      label: 'Mi Perfil', 
      icon: UserCircle,
      description: 'Edita tu teléfono y domicilio'
    },
    { 
      path: '/alumno/documentos', 
      label: 'Mis Documentos', 
      icon: FileUp,
      description: 'Sube y consulta tus documentos'
    },
    { 
      path: '/alumno/tramites', 
      label: 'Trámites', 
      icon: Stamp,
      description: 'Solicita constancias y extraordinarios'
    },
    { 
      path: '/alumno/horarios', 
      label: 'Horarios y Materiales', 
      icon: Calendar,
      description: 'Calendario, aulas, docentes y materiales'
    },
    { 
      path: '/alumno/calificaciones', 
      label: 'Mis Calificaciones', 
      icon: BookOpen,
      description: 'Consulta tu avance académico por materia'
    },
    { 
      path: '/alumno/facturacion', 
      label: 'Mis Facturas', 
      icon: Receipt,
      description: 'Autofacturación y datos fiscales'
    },
    { 
      path: '/alumno/comprobante-pago', 
      label: 'Comprobante de Pago', 
      icon: Upload,
      description: 'Envía tu comprobante de transferencia o depósito'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile menu overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </div>
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b">
            <img src="/qcampusone-logo.png" alt="QCampusOne" className="h-10 w-auto" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {/* Main menu items */}
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors",
                    isActive
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-700 hover:bg-gray-100"
                  )}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.label}
                </Link>
              );
            })}

            {/* Autoservicio Dropdown */}
            <div className="space-y-1">
              <button
                onClick={() => setAutoservicioOpen(!autoservicioOpen)}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors",
                  autoservicioItems.some(item => location.pathname === item.path)
                    ? "bg-blue-50 text-blue-700"
                    : "text-gray-700 hover:bg-gray-100"
                )}
              >
                <div className="flex items-center">
                  <User className="w-5 h-5 mr-3" />
                  <span>Autoservicio</span>
                </div>
                {autoservicioOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>

              {/* Autoservicio Submenu */}
              {autoservicioOpen && (
                <div className="ml-4 pl-4 border-l-2 border-gray-200 space-y-1">
                  {autoservicioItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={cn(
                          "flex items-start px-3 py-2 text-sm rounded-md transition-colors group",
                          isActive
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        )}
                      >
                        <Icon className="w-4 h-4 mr-3 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{item.label}</div>
                          <div className="text-xs text-gray-500 mt-0.5 group-hover:text-gray-600">
                            {item.description}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>

          {/* User info and logout */}
          <div className="p-4 border-t">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user.fullName}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="w-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 w-full">
        {/* Mobile header */}
        <div className="lg:hidden h-16 bg-white border-b flex items-center px-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </Button>
          <img src="/qcampusone-logo.png" alt="QCampusOne" className="h-8 w-auto ml-4" />
        </div>

        {/* Page content */}
        <main className="p-4 md:p-6 w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
